import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/loading; false = anon; obj = admin
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Retry up to 3 times on network errors (handles backend hot-reload blips)
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/api/auth/me");
        setUser(data);
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // Auth errors (401/403) -> clear token immediately, don't retry
        if (status === 401 || status === 403) {
          tokenStore.clear();
          setUser(false);
          setLoading(false);
          return;
        }
        // Network/5xx -> wait and retry
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    // After retries failed: keep token (might recover) but mark as anon for now
    console.warn("Auth check failed after retries", lastErr);
    tokenStore.clear();
    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      const status = err?.response?.status;
      let error;
      if (status === 401) {
        error = "Correo o contraseña incorrectos.";
      } else if (!err?.response) {
        error =
          "No se pudo conectar con el servidor. Revise su conexión o intente de nuevo en unos segundos.";
      } else {
        error = formatApiError(err, "No se pudo iniciar sesión.");
      }
      return { ok: false, error };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/api/auth/register", payload);
      tokenStore.set(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      return {
        ok: false,
        error: formatApiError(err, "No se pudo crear la cuenta."),
      };
    }
  };

  const updateProfile = async (payload) => {
    try {
      const { data } = await api.put("/api/auth/me", payload);
      setUser((u) => ({ ...(u || {}), ...data }));
      return { ok: true, user: data };
    } catch (err) {
      return {
        ok: false,
        error: formatApiError(err, "No se pudo actualizar el perfil."),
      };
    }
  };

  const logout = () => {
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, updateProfile, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
